/**
 * Sign in.
 *
 * The form only collects credentials; every decision about them is the
 * server's. Failures are reported exactly as the server phrased them, which
 * deliberately does not distinguish an unknown account from a wrong password.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useAuth } from '../lib/auth';
import { ApiError } from '../lib/api';
import { Alert, Button, Field, Input } from '../components/ui';
import { LotusMark } from '../components/AppLayout';

const schema = z.object({
  email: z.string().min(1, 'Enter your email address').email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password'),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (values: FormValues) => {
    setFormError(null);
    try {
      await login(values.email, values.password);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? '/', { replace: true });
    } catch (error) {
      if (error instanceof ApiError) {
        setFormError(
          error.status === 429
            ? 'Too many sign-in attempts. Please wait a few minutes and try again.'
            : error.message,
        );
      } else {
        setFormError('Could not reach the server. Please try again.');
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-tint px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <LotusMark size={40} />
          <h1 className="mt-3 text-lg font-semibold text-ink">
            Proposal Generator
          </h1>
          <p className="mt-1 text-[13px] text-muted">Lotus Tech Solutions Inc.</p>
        </div>

        <div className="rounded-lg border border-rule bg-white p-6">
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            {formError && <Alert tone="error">{formError}</Alert>}

            <Field label="Email" htmlFor="email" error={errors.email?.message} required>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                autoFocus
                aria-invalid={Boolean(errors.email)}
                {...register('email')}
              />
            </Field>

            <Field label="Password" htmlFor="password" error={errors.password?.message} required>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={Boolean(errors.password)}
                {...register('password')}
              />
            </Field>

            <Button type="submit" loading={isSubmitting} className="w-full">
              Sign in
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-[12px] text-muted">
          Lotus Tech Solutions Inc. — Confidential
        </p>
      </div>
    </div>
  );
}
